import React from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ErrorInfo, ERROR_TYPE } from '#shared/types/common';
import StandardLayoutCommon from '#web-components/components/Layouts/Standard';
import { getRegistryTitle, getRegistryTitleFull } from '#shared/utils/registrySettings';
import LogoLink from '#web-components/components/Navbar/components/LogoLink';

import { selectorAsyncActionCriticalErrors } from 'store/asyncAction/selectors';
import NavLinks from 'components/Navbar/components/NavLinks';
import Menu from 'components/Menu';
import LoaderIcon from 'components/LoaderImage';
import LogoIcon from 'components/LogoImage';

import ErrorLayout from '../Error';

interface Props {
  title: string;
  children?: React.ReactNode;
  description?: string;
  titleVariant?: string;
  isLoading?: boolean;
  error?: ErrorInfo;
  backLink?: {
    title: string,
    path: string,
    RouterNavLink: React.ElementType,
  };
}

export default function StandardLayout({
  title,
  description,
  error,
  backLink,
  titleVariant,
  isLoading = false,
  children,
}: Props) {
  const { t } = useTranslation('domains');
  const criticalError = useSelector(selectorAsyncActionCriticalErrors);
  const hasError = criticalError || (error && (!error.type || error.type === ERROR_TYPE.CRITICAL));
  const registryTitle = getRegistryTitle(REGISTRY_SETTINGS);
  const registryTitleFull = getRegistryTitleFull(REGISTRY_SETTINGS);

  if (hasError) {
    return (
      <ErrorLayout
        error={criticalError || error}
      />
    );
  }

  return (
    <StandardLayoutCommon
      t={t}
      title={title}
      titleVariant={titleVariant}
      registryName={registryTitle}
      registryFullName={registryTitleFull}
      description={description}
      backLink={backLink}
      isLoading={isLoading}
      loaderIcon={<LoaderIcon />}
      sidebar={{
        logo: (
          <LogoLink
            appTitle={t('text:appTitle')}
            homePath="/"
            logoIcon={<LogoIcon />}
            navLinkComponent={RouterLink}
          />
        ),
        navLinks: <NavLinks />,
        menu: <Menu />,
      }}
      supportChannelUrl={REGISTRY_ENVIRONMENT_VARIABLES.supportChannelUrl}
    >
      {children}
    </StandardLayoutCommon>
  );
}
